import moment from 'moment'
import React from 'react'
import * as Utils from './utils'
import { DateFnsFormats, formatAs, getTimeOnly, getAsStartAndEndTimeFormat } from '../common-client2/src/services/ukgDateTime';
import { ShiftSegmentTypeName, PostedRequestDisplay, RequestTypes } from './Enums'
import i18n from '../i18n';

/**
 *  @brief Create a time label of start{h:mm A} - end{h:mm A}
 *  @param[in] shift the shift retrieved from .NET backend
 */
export function createTimeLabelFromShift(shift) {
    const transferSegment = (shift.segments) ? shift.segments.find(segment => segment.type === ShiftSegmentTypeName.TRANSFER_SEGMENT) : null;
    return `${getAsStartAndEndTimeFormat(shift.startDateTime, shift.endDateTime)}${(transferSegment ? ' (x)' : '')}`
}

/**
 *  @brief Determine if time is before today
 *  @param[in] _endTime_ time given either in moment or not
 *
 *  @return if is before today.
*/
export function isBeforeToday(endTime) {
    let today = new moment();
    let end = moment.utc(endTime);
    return (end.diff(today) < 0) ? true : false;
}

/**
 * @brief Use `(typeof _object_ === "undefined")` to check for undefined
 * @param object javascript object to check
 *
 * @return if object is undefined
 */

export function isNullOrUndefined(object) {
    return (object === undefined || object === null)
}

/**
 * @brief Get the primary job from the shift
 * @param {object} shiftObject
 */
export function getFirstPrimaryJobFromSegments(shiftObject) {
    var ret = shiftObject.segments.find(
         
        function (seg) {
            if (seg.shortOrgJobPath) {
                return seg.shortOrgJobPath;
            }
        }
    );
    return ret?.shortOrgJobPath;
}

export const formatShift = (shiftList) => {
    return shiftList.map(ele => {
        return {
            id: ele.id, //Used for <SliderPartialRequest />
            shiftTime: getAsStartAndEndTimeFormat(ele.startDateTime, ele.endDateTime),
            shiftDate: formatAs(ele.startDateTime, DateFnsFormats.VERBOSE_LONG_DATE_FORMAT),
            fullTime: ele.startDateTime,
            primaryJob: getFirstPrimaryJobFromSegments(ele),
            startDateTime: ele.startDateTime, //Used for <SliderPartialRequest />
            endDateTime: ele.endDateTime, //Used for <SliderPartialRequest />
            label: ele.label,
        }
    })
}

export const formatProposalData = (proposal) => {
    let data = {
        'proposalId': proposal['proposalId'],
        'date': formatAs(proposal['proposalDate'], DateFnsFormats.VERBOSE_LONG_DATE_FORMAT),
        'time': getTimeOnly(proposal['proposalDate']),
        'location': Utils.parseJobAndLocation(proposal['proposalEmployeeJob']).location,
        'name': proposal['proposalEmployeeName'],
        'shifts': proposal['shifts'],
        isPartial: proposal.isPartial,
        partialShift: proposal.partialShift,
        'status': proposal['status'],
        'selectedAssignmentName': proposal['selectedAssignmentName'],
    }
    return data;
}

export const formatProposalDataWithCustomName = (proposal, name) => {
    let data = {
        'proposalId': proposal['proposalId'],
        'date': formatAs(proposal['proposalDate'], DateFnsFormats.VERBOSE_LONG_DATE_FORMAT),
        'time': getTimeOnly(proposal['proposalDate']),
        'location': Utils.parseJobAndLocation(proposal['proposalEmployeeJob']).location,
        'name': name,
        'shifts': proposal['shifts'],
        isPartial: proposal.isPartial,
        partialShift: proposal.partialShift,
        'status': proposal['status']
    }
    return data;
}

export const getRequestTypeString = (requestType) => {
    let statusValue = [
        "To Cover",
        "To Swap",
        "Open Shift",
    ]
    return statusValue[requestType];
}

export const handleAPIErrors = (response) => {
    if (response) {
        if (response.data?.Message) {
            return response.data.Message;
        }
        else if (response.data?.message) {
            return response.data.message;
        }
        else if (response.status) {
            if (response.status === 200 && response.data?.fail?.length > 0 && response.data.details) {
                let message = (
                    <React.Fragment>
                        {parseMultiFailureMessage(response.data.details)}
                    </React.Fragment>
                )
                return message
            }
            if (response.status === 500) {
                return 'Internal server error. Please contact a system administrator.';
            }
            return undefined
        }
        else {
            return 'An error occured while trying to load the schedule. Please try refreshing the page.'
        }
    }
    else {
        return 'An error occured. Please contact a system administrator.'
    }
}

const parseMultiFailureMessage = (details, max = 5) => {
    if (details.length > max - 1) {
        return 'Multiple shifts are in conflicts'
    }

    let messages = new Map();
     
    for (const [_, detail] of details.entries()) {
        let msg = messages.get(detail.message.trim());

        if (msg === undefined || msg === null) {
            messages.set(detail.message.trim(), 1);
        } else {
            messages.set(detail.message.trim(), ++msg);
        }
    }

    let retour = [];
    for (const [errMsg, value] of messages) {
        if (retour.length > max) {
            break;
        }

        if (value > 1) {
            retour.push(
                <React.Fragment>
                    {"(" + value + "x) " + errMsg}
                    {(max - 1 !== retour.length) ? <br /> : null}
                </React.Fragment>
            )
        } else {
            retour.push(
                <React.Fragment>
                    {errMsg}
                    {(max - 1 !== retour.length) ? <br /> : null}
                </React.Fragment>
            )
        }
    }

    return retour
}

export const formatPath = (path, number) => {
    if (path) {
        let locationSplit = path.split('/')
        let locationShort = path
        if (locationSplit.length > number) {
            let auxIterator = 0;
            locationShort = ''
            for (let i = locationSplit.length - 1; i >= 0 && auxIterator < number; i--) {
                locationShort = '/' + locationSplit[i] + locationShort
                auxIterator++
            }
            locationShort = locationSplit.length > auxIterator ? '...' + locationShort : locationShort
        }
        return locationShort;
    }
    return '';
}

export const hasTransfer = (shift) => {
    return shift.segments?.filter(segment => segment.type === ShiftSegmentTypeName.TRANSFER_SEGMENT).length > 0
}

/**
 * @param {string} name
 * @param {string[]} allNames
 * @param {string} copyPrefix
 */
export const createCopyName = (name = '', allNames = [], copyPrefix = 'Copy of') => {
    if (!name.startsWith(copyPrefix)) {
        return `${copyPrefix} ${name}`;
    }

    const regexp = /(?:\((\d*)\)){0,1}$/;
    const match = name.match(regexp);

    let currentCopyNumber = 1;
    /** @type{string} */
    let originalName = name;
    if (match[1] !== undefined) {
        originalName = name.substring(0, name.lastIndexOf(` (${match[1]})`));
        currentCopyNumber = parseInt(match[2]) + 1;
    }

    const existingCopies = allNames.filter(x => x.startsWith(`${originalName}`) && x.match(regexp)[1] !== undefined).sort();

    if (existingCopies.length === 0) {
        return `${originalName} (${currentCopyNumber})`;
    }

    const lastCopy = existingCopies[existingCopies.length - 1];
    const lastMatch = lastCopy.match(regexp);
    currentCopyNumber = lastMatch[1] !== undefined ? parseInt(lastMatch[1]) + 1 : 1;

    return `${originalName} (${currentCopyNumber})`;
}

/**
 * @param {number} postedRequestDisplay
 * @param {Shift.Shift} shift
 * @param {any} managerSetting
 */
export const getScheduleTitle = (postedRequestDisplay, shift, managerSetting) => {
    if (postedRequestDisplay === PostedRequestDisplay.requestName || managerSetting.type == RequestTypes.voluntaryTimeOff) {
        return managerSetting.name;
    }
    else if (postedRequestDisplay === PostedRequestDisplay.shiftTime) {
        return ((shift.label) ? shift.label + ' ' : '') + createTimeLabelFromShift(shift);
    }

    return '';
};